import { Injectable } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import {
  LoadManifest,
  LoadManifestSuccess,
  LoadManifestFailure,
  LoadFragments,
  LoadFragmentsSuccess,
  LoadFragmentsFailure,
  SaveFragment,
  SaveFragmentSuccess,
  SaveFragmentFailure,
  RemoveFragment,
  RemoveFragmentSuccess,
  RemoveFragmentFailure,
  UserManualActionTypes,
} from '@completion/actions';
import { UserManualFragment } from '@completion/models';
import { catchError, map, mergeMap, of } from 'rxjs';
import { UserManualServiceCore } from '@completion/services';

function normalizeOrdinal(raw?: string): string | undefined {
  if (!raw) return undefined;
  // split on dots, keep up to 10 segments, pad each numeric seg to 3 digits, leave non-numeric as-is but trimmed
  const parts = raw
    .split('.')
    .map((p) => p.trim())
    .filter((p) => p.length > 0);
  if (!parts.length) return undefined;
  const padded = parts.map((p) => {
    const n = parseInt(p, 10);
    if (!isNaN(n)) {
      return n.toString().padStart(3, '0');
    }
    // keep non-numeric segments, but limit length
    return p.slice(0, 10);
  });
  return padded.join('.');
}

const STORAGE_MANIFEST_KEY = 'app_user_manual_manifest_v1';
const STORAGE_MANIFEST_HASH_KEY = 'app_user_manual_manifest_hash_v1';

@Injectable()
export class UserManualEffects {
  loadManifest$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadManifest>(UserManualActionTypes.LoadManifest),
      mergeMap((action: LoadManifest) => {
        const force = !!(action && (action as any).force);
        if (force) {
          return this.userManualService.getManifest().pipe(
            map((res) => {
              try {
                localStorage.setItem(STORAGE_MANIFEST_HASH_KEY, res.hash || '');
                localStorage.setItem(STORAGE_MANIFEST_KEY, JSON.stringify(res.items || []));
              } catch {}
              return new LoadManifestSuccess(res.items || [], res.hash);
            }),
            catchError((error) => of(new LoadManifestFailure(error))),
          );
        }

        // normal path: compare server hash with local hash
        return this.userManualService.getManifestHash().pipe(
          mergeMap((hres) => {
            const serverHash = hres && hres.hash ? hres.hash : undefined;
            const localHash = localStorage.getItem(STORAGE_MANIFEST_HASH_KEY) || undefined;
            if (serverHash && localHash && serverHash === localHash) {
              // no change -> read manifest from localStorage and dispatch success
              try {
                const raw = localStorage.getItem(STORAGE_MANIFEST_KEY);
                const items = raw ? JSON.parse(raw) : [];
                return of(new LoadManifestSuccess(items || [], localHash));
              } catch {
                // parse failure -> fall through to fetching full manifest
              }
            }
            // fetch full manifest from server
            return this.userManualService.getManifest().pipe(
              map((res) => {
                try {
                  localStorage.setItem(STORAGE_MANIFEST_HASH_KEY, res.hash || '');
                  localStorage.setItem(STORAGE_MANIFEST_KEY, JSON.stringify(res.items || []));
                } catch {}
                return new LoadManifestSuccess(res.items || [], res.hash);
              }),
              catchError((error) => of(new LoadManifestFailure(error))),
            );
          }),
          catchError((error) => {
            // network/hash endpoint error -> try local storage then fail
            try {
              const raw = localStorage.getItem(STORAGE_MANIFEST_KEY);
              const items = raw ? JSON.parse(raw) : [];
              const localHash = localStorage.getItem(STORAGE_MANIFEST_HASH_KEY) || undefined;
              return of(new LoadManifestSuccess(items || [], localHash));
            } catch {
              return of(new LoadManifestFailure(error));
            }
          }),
        );
      }),
    ),
  );

  loadFragments$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadFragments>(UserManualActionTypes.LoadFragments),
      mergeMap((action: LoadFragments) =>
        this.userManualService.getFragments(action.ids).pipe(
          map(
            (res) =>
              new LoadFragmentsSuccess(
                (res.fragments || []).map(
                  (f: any) =>
                    ({
                      id: f.id,
                      key: f.key,
                      html: f.html,
                      lastModified: f.lastModified,
                      hash: f.hash,
                      ordinal: normalizeOrdinal(f.ordinal),
                    }) as UserManualFragment,
                ),
              ),
          ),
          catchError((error) => of(new LoadFragmentsFailure(error))),
        ),
      ),
    ),
  );

  // When manifest successfully loads, trigger fragments load for the items
  loadManifestSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadManifestSuccess>(UserManualActionTypes.LoadManifestSuccess),
      map((action: LoadManifestSuccess) => {
        // Prefer numeric DB id (String(id)) for fetches; fall back to manualId only if id missing
        const rawItems: any = action && (action as any).items ? (action as any).items : [];
        const itemsArr: any[] = Array.isArray(rawItems) ? rawItems : typeof rawItems === 'object' ? Object.values(rawItems) : [];
        const ids = itemsArr
          .map((i) => (i && typeof i.id === 'number' ? String(i.id) : i && i.manualId ? i.manualId : undefined))
          .filter(Boolean) as string[];
        try {
          console.debug('[UserManualEffects] dispatching LoadFragments for ids', ids);
        } catch {}
        return new LoadFragments(ids);
      }),
    ),
  );

  saveFragment$ = createEffect(() =>
    this.actions$.pipe(
      ofType<SaveFragment>(UserManualActionTypes.SaveFragment),
      mergeMap((action: SaveFragment) => {
        const f = action.fragment || ({} as UserManualFragment);
        // normalize ordinal if present
        if (f.ordinal) {
          f.ordinal = normalizeOrdinal(f.ordinal) as any;
        }
        // choose POST (create) vs PUT (update) depending on presence of numeric id
        const useUpdate = f && typeof (f as any).id !== 'undefined' && (f as any).id !== null;
        const call$ = useUpdate ? this.userManualService.updateFragment(f as any) : this.userManualService.saveFragment(f as any);
        return call$.pipe(
          map((res: any) => {
            const out: any = {
              id: res.id,
              key: res.key,
              html: res.html,
              lastModified: res.lastModified,
              hash: res.hash,
              ordinal: normalizeOrdinal(res.ordinal),
            };
            return new SaveFragmentSuccess(out as UserManualFragment);
          }),
          catchError((error) => of(new SaveFragmentFailure(error))),
        );
      }),
    ),
  );

  removeFragment$ = createEffect(() =>
    this.actions$.pipe(
      ofType<RemoveFragment>(UserManualActionTypes.RemoveFragment),
      mergeMap((action: RemoveFragment) =>
        this.userManualService.deleteFragment(action.manualId).pipe(
          map(() => new RemoveFragmentSuccess(action.manualId)),
          catchError((error) => of(new RemoveFragmentFailure(error))),
        ),
      ),
    ),
  );

  constructor(
    private actions$: Actions,
    private userManualService: UserManualServiceCore,
  ) {}
}
